import { AddCircleOutline, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  TextField,
  Typography,
  DialogContent,
} from "@mui/material";
import { useEffect, useState } from "react";
import AppButton from "../../../../base/AppButton.component";
import Theme from "../../../../../utils/Theme";
import { SalesAidsStyles } from "./SalesAidsStyles";
import { makeInfoItem } from "./warrantyConstants";

/**
 * I-button popup — covered items table editor.
 * Title / Subtitle / Footer on the coverage row;
 * each info item = one table column (header + bullet list).
 *
 * Syncs to parent on every change (no Submit) — same as before.
 */
const WarrantyInfoPopup = ({ initialItems, initialMeta, onSave }) => {
  const [meta, setMeta] = useState(() => ({
    info_title_en: initialMeta?.info_title_en || "",
    info_title_es: initialMeta?.info_title_es || "",
    info_subtitle_en: initialMeta?.info_subtitle_en || "",
    info_subtitle_es: initialMeta?.info_subtitle_es || "",
    info_footer_en: initialMeta?.info_footer_en || "",
    info_footer_es: initialMeta?.info_footer_es || "",
  }));

  const [items, setItems] = useState(() => {
    if (initialItems?.length) {
      return initialItems.map((item) => ({
        ...makeInfoItem(),
        ...item,
        id: item.id || makeInfoItem().id,
        information_item_id: item.information_item_id ?? null,
      }));
    }
    return [makeInfoItem()];
  });

  useEffect(() => {
    onSave?.({
      ...meta,
      infoItems: items,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, meta]);

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  const handleChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddColumn = () => {
    setItems((prev) => [...prev, makeInfoItem()]);
  };

  const handleRemoveColumn = (id) => {
    setItems((prev) =>
      prev.length <= 1 ? prev : prev.filter((item) => item.id !== id)
    );
  };

  return (
    <DialogContent>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          Covered Items (I)
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography sx={{ ...SalesAidsStyles.wordStyle, flex: 1 }}>
            English
          </Typography>
          <Typography sx={{ ...SalesAidsStyles.wordStyle, flex: 1 }}>
            Spanish
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Title"
            value={meta.info_title_en}
            onChange={(e) => handleMetaChange("info_title_en", e.target.value)}
            size="medium"
            fullWidth
          />
          <TextField
            label="Título"
            value={meta.info_title_es}
            onChange={(e) => handleMetaChange("info_title_es", e.target.value)}
            size="medium"
            fullWidth
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Subtitle"
            value={meta.info_subtitle_en}
            onChange={(e) =>
              handleMetaChange("info_subtitle_en", e.target.value)
            }
            size="medium"
            fullWidth
          />
          <TextField
            label="Subtítulo"
            value={meta.info_subtitle_es}
            onChange={(e) =>
              handleMetaChange("info_subtitle_es", e.target.value)
            }
            size="medium"
            fullWidth
          />
        </Box>

        <Divider />

        <Typography variant="subtitle2" fontWeight={600}>
          Columns
        </Typography>

        {items.map((item, index) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              position: "relative",
            }}
          >
            {index > 0 ? <Divider sx={{ mb: 0.5 }} /> : null}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Column {index + 1}
              </Typography>
              {items.length > 1 ? (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveColumn(item.id)}
                  sx={{ ml: "auto", color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <TextField
                label="Column Header"
                value={item.coverage_category_en}
                onChange={(e) =>
                  handleChange(item.id, "coverage_category_en", e.target.value)
                }
                size="medium"
                fullWidth
              />
              <TextField
                label="Encabezado de Columna"
                value={item.coverage_category_es}
                onChange={(e) =>
                  handleChange(item.id, "coverage_category_es", e.target.value)
                }
                size="medium"
                fullWidth
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Column Items (one per line)"
                value={item.coverage_items_en}
                onChange={(e) =>
                  handleChange(item.id, "coverage_items_en", e.target.value)
                }
                size="medium"
                fullWidth
                multiline
                minRows={4}
                helperText="Enter each bullet on its own line"
              />
              <TextField
                label="Artículos de Columna (uno por línea)"
                value={item.coverage_items_es}
                onChange={(e) =>
                  handleChange(item.id, "coverage_items_es", e.target.value)
                }
                size="medium"
                fullWidth
                multiline
                minRows={4}
              />
            </Box>
          </Box>
        ))}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 0.5 }}>
          <AddCircleOutline
            sx={{ fontSize: 18, color: Theme.palette.primary.main }}
          />
          <AppButton
            variant="text"
            buttonText="Add Column"
            size="small"
            sx={{ textDecoration: "underline" }}
            clickHandler={handleAddColumn}
          />
        </Box>

        <Divider />

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Footer"
            value={meta.info_footer_en}
            onChange={(e) => handleMetaChange("info_footer_en", e.target.value)}
            size="medium"
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Pie de página"
            value={meta.info_footer_es}
            onChange={(e) => handleMetaChange("info_footer_es", e.target.value)}
            size="medium"
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      </Box>
    </DialogContent>
  );
};

export default WarrantyInfoPopup;
